import React, {useReducer, createContext, useContext, useCallback} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    TextField,
} from '@mui/material';
import PropTypes from "prop-types";
import _ from "lodash";

const ActionType = {
    SetConfirm: "SET_CONFIRM",
    SetModal: "SET_MODAL",
    SetAlert: "SET_ALERT",
    SetInput: "SET_INPUT",
    Close: "CLOSE",
};

const initialState = {
    open: false,
    type: null,
    title: '',
    message: '',
    onOk: null,
    onConfirm: null,
    onCancel: null,
    inputValue: '',
    inputLabel: '',
    autoClose: true,
};

const DialogReducer = (state, action) => {
    switch (action.type) {
        case ActionType.SetConfirm:
            return {
                ...state,
                open: true,
                type: 'confirm',
                title: action.payload.title || '確認',
                message: action.payload.message,
                onConfirm: action.payload.onConfirm ?? action.payload.onOk,
                onCancel: action.payload.onCancel,
                autoClose: action.payload.autoClose ?? true,
            };

        case ActionType.SetModal:
            return {
                ...state,
                open: true,
                type: 'modal',
                title: action.payload.title || '確認',
                message: action.payload.message,
                onConfirm: action.payload.onOk,
                onCancel: action.payload.onCancel,
                autoClose: action.payload.autoClose ?? true,
            };

        case ActionType.SetAlert:
            return {
                ...state,
                open: true,
                type: 'alert',
                title: action.payload.title || 'お知らせ',
                message: action.payload.message,
                onConfirm: action.payload.onOk,
                autoClose: action.payload.autoClose ?? true,
            };

        case ActionType.SetInput:
            return {
                ...state,
                open: true,
                type: 'input',
                title: action.payload.title || '入力',
                message: action.payload.message,
                inputValue: action.payload.defaultValue || '',
                inputLabel: action.payload.inputLabel || '入力してください',
                onConfirm: action.payload.onOk,
                onCancel: action.payload.onCancel,
                autoClose: action.payload.autoClose ?? true,
            };

        case ActionType.Close:
            return {
                ...initialState,
            };

        default:
            return state;
    }
};

// Context作成
const DialogContext = createContext();

// DialogProvider コンポーネント
export const DialogProvider = ({ children }) => {
    const [state, dispatch] = useReducer(DialogReducer, initialState);

    // ダイアログを開く関数群
    const openConfirm = useCallback((message, options) => {
        dispatch({
            type: ActionType.SetConfirm,
            payload: {
                message,
                ...options
            },
        })
    }, [])

    const openModal = useCallback((message, options) => {
        dispatch({
            type: ActionType.SetModal,
            payload: {
                message,
                ...options
            },
        })
    }, [])

    const openAlert = useCallback((message, options) => {
        dispatch({
            type: ActionType.SetAlert,
            payload: {
                message,
                ...options
            },
        })
    }, [])

    const openDialog = openAlert

    const openInput = useCallback((message, options) => {
        dispatch({
            type: ActionType.SetInput,
            payload: {
                message,
                ...options
            },
        })
    }, [])

    const closeDialog = useCallback(() => {
        dispatch({ type: ActionType.Close })
    }, [])

    // ボタンハンドラー
    const handleConfirm = useCallback(() => {
        let f = null
        if  (state.onConfirm) {
            if (state.type === "input"){
                f = state.onConfirm(state.inputValue)
            } else {
                f = state.onConfirm()
            }
        }
        if (_.isNil(f)) {
            state.autoClose && closeDialog()
        } else {
            Promise.resolve(f)
                .then(close => {
                    if (close || state.autoClose) {
                        closeDialog()
                    }
                })
        }
    }, [closeDialog, state.type, state.onConfirm, state.autoClose, state.inputValue])

    const handleCancel = useCallback(() => {
        let f = null
        if (state.onCancel) {
            f = state.onCancel()
        }
        if (_.isNil(f)) {
            state.autoClose && closeDialog()
        } else {
            Promise.resolve(f)
                .then(close => {
                    if (close || state.autoClose) {
                        closeDialog()
                    }
                })
        }
    }, [state.onCancel, state.autoClose, closeDialog])

    const handleInputChange = useCallback((e) => {
        dispatch({
            type: ActionType.SetInput,
            payload: {
                ...state,
                inputValue: e.target.value,
            },
        });
    }, [state])

    const contextValue = {
        openConfirm,
        openDialog,
        openModal,
        openAlert,
        openInput,
        closeDialog,
    };

    return (
        <DialogContext.Provider value={contextValue}>
            {children}

            <Dialog
                open={state.open}
                onClose={state.type === 'alert' ? handleConfirm : handleCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{state.title}</DialogTitle>

                <DialogContent>
                    {state.message && (
                        React.isValidElement(state.message) ?
                            state.message :
                            (
                                <DialogContentText style={{whiteSpace: "pre-line"}}>
                                    {String(state.message)}
                                </DialogContentText>
                            )
                    )}

                    {state.type === 'input' && (
                        <TextField
                            autoFocus
                            margin="dense"
                            label={state.inputLabel}
                            fullWidth
                            variant="outlined"
                            value={state.inputValue}
                            onChange={handleInputChange}
                        />
                    )}
                </DialogContent>

                <DialogActions>
                    {state.type === 'confirm' && (
                        <>
                            <Button onClick={handleCancel}>いいえ</Button>
                            <Button onClick={handleConfirm} variant="contained">
                                はい
                            </Button>
                        </>
                    )}

                    {state.type === 'modal' && (
                        <>
                            <Button onClick={handleCancel}>Cancel</Button>
                            <Button onClick={handleConfirm} variant="contained">
                                OK
                            </Button>
                        </>
                    )}

                    {state.type === 'alert' && (
                        <Button onClick={handleConfirm} variant="contained">
                            OK
                        </Button>
                    )}

                    {state.type === 'input' && (
                        <>
                            <Button onClick={handleCancel}>キャンセル</Button>
                            <Button onClick={handleConfirm} variant="contained">
                                OK
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </DialogContext.Provider>
    );
};
DialogProvider.propTypes = {
    children: PropTypes.node.isRequired,
}

// カスタムフック
export const useDialog = () => {
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error('useDialog must be used within a DialogProvider');
    }
    return context;
};